// Renders the four "before-confirm" stages: idle, picking, uploading,
// parsing. Receives state + handlers from the hook; no local state.

export default function PickingUI({ state, handlers, inputRef, isLoading }) {
  const { stage, files, fileIds, progress } = state;
  const { goPicking, handleFileSelect, handleRemoveFile, reset, handleParse } =
    handlers;

  if (stage === "idle") {
    return (
      <button
        type="button"
        disabled={isLoading}
        onClick={goPicking}
        className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-2 text-sm font-medium text-textcolor shadow-sm hover:bg-gray-50 disabled:opacity-50"
      >
        <span aria-hidden="true">📄</span>
        Import from Purchase Agreement
      </button>
    );
  }

  if (stage === "picking") {
    return (
      <div className="rounded-xl border border-black/10 bg-white p-4 shadow-sm">
        <div
          className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center"
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            handleFileSelect(e.dataTransfer.files);
          }}
        >
          <p className="text-sm font-medium text-textcolor">
            Drop PDFs here or click to choose files
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Add the Purchase Agreement and any Counter-offers.
          </p>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="mt-3 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-sm hover:bg-gray-100"
          >
            Choose files
          </button>
          <input
            ref={inputRef}
            data-testid="file-input"
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>

        {files.length > 0 && (
          <ul className="mt-3 space-y-1">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="flex items-center justify-between rounded-md border border-black/5 bg-gray-50 px-3 py-1.5 text-sm"
              >
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(i)}
                  className="ml-2 text-xs text-gray-500 hover:text-red-600"
                  aria-label={`Remove ${f.name}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={reset}
            className="rounded-lg border border-black/10 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={files.length === 0}
            onClick={handleParse}
            className="rounded-lg bg-[#1E293B] px-3 py-1.5 text-sm text-white hover:bg-[#0f172a] disabled:opacity-50"
          >
            Parse Documents
          </button>
        </div>
      </div>
    );
  }

  if (stage === "uploading") {
    return (
      <div className="rounded-xl border border-black/10 bg-white p-4 shadow-sm">
        <p className="mb-2 text-sm font-medium text-textcolor">
          Uploading documents…
        </p>
        <ul className="space-y-2">
          {files.map((f, i) => {
            const fid = fileIds[i];
            const pct = Math.round(((progress[fid] || 0) * 100) || 0);
            return (
              <li key={fid || `${f.name}-${i}`}>
                <div className="flex justify-between text-xs text-gray-600">
                  <span className="truncate">{f.name}</span>
                  <span>{pct}%</span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full bg-[#1E293B]"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  if (stage === "parsing") {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-black/10 bg-white p-4 shadow-sm">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-[#1E293B]" />
        <p className="text-sm font-medium text-textcolor">
          Reading purchase agreement…
        </p>
      </div>
    );
  }

  return null;
}
